/**
 * Customer price cards the platform granted this reseller (TMMAPI-0131, MM-D-108; one card set by
 * the platform, TMMAPI-0154, MM-D-136), reseller-wide on `/v1/reseller-admin/rate-cards`. The
 * reseller lists and reads them, adopts one for new sales, and asks for a version's margin worksheet.
 * Nothing here is editable. Any role may GET and ask for a worksheet; adopt is OWNER or ADMIN.
 */
import { request } from './client';
import type {
  GrantedBandView,
  GrantedCardControls as GrantedCardControls_Gen,
  GrantedCustomerRateCardList as GrantedCustomerRateCardList_Gen,
  GrantedCustomerRateCardView as GrantedCustomerRateCardView_Gen,
  ResellerWorksheet,
  ResellerWorksheetCharge,
  ResellerWorksheetLine,
  ResellerWorksheetSteps,
  WorksheetBill,
  WorksheetBillLine,
  WorksheetRequest,
} from './generated';

/**
 * The adopted card's maximums for the reseller's commission plans (TMMAPI-0154 §I, the granted
 * card's `controls`), money in the card's `currency`. Checked by tmm-api at plan create and
 * activate; active plans are never changed.
 */
export interface AffiliateMaximums {
  recurringMaxBp: number;
  bountyMaxMinor: number;
  milestoneMaxMinor: number;
  coolOffMaxDays: number;
  currency: string;
}

/** The card's live controls in force (TMMAPI-0153), the parts this console reads. */
export interface GrantedControls {
  graceDaysAfterEnd: number;
  affiliate: AffiliateMaximums;
}

/**
 * One granted card (`GrantedCustomerRateCardView`): the customer's prices (retail) and, since
 * TMMAPI-0155, the reseller's own cost of each (wholesale). Our AI wholesale markup is never sent.
 * Required money is the number tmm-api sent. `null` is only a field the contract marks nullable.
 */
export interface GrantedCard {
  id: number;
  name: string;
  currency: string;
  currentVersionId: number | null;
  adopted: boolean;
  /** `null` when there is no agreement. */
  marginMinor: number | null;
  /** Whether tmm-api sent an example bill: with the margin, the sign of an agreement. */
  hasExample: boolean;
  baseMinor: number;
  /** The reseller's cost (wholesale) of the monthly base (TMMAPI-0155). */
  baseWholesaleMinor: number;
  /** `null` when the card has no annual base. */
  annualBaseMinor: number | null;
  /** The reseller's cost (wholesale) of the annual base; tmm-api sends 0 without an annual base. */
  annualBaseWholesaleMinor: number;
  trialDaysNewSubscriber: number;
  trialDaysGuestConversion: number;
  /** The retail markup over our AI price, in bp (TMMAPI-0154, was `aiMarkupBp`). */
  aiRetailMarkupBp: number;
  /** The monthly AI credit at the customer's price. */
  aiMonthlyCreditMinor: number;
  /** The platform's share of the credit's cost, in bp. */
  aiCreditPlatformShareBp: number;
  /** What one automatic wallet recharge charges. */
  aiRechargeAmountMinor: number;
  /** Each band's retail and wholesale. `to` is `null` when the band is open-ended. */
  seatBands: GrantedBandView[];
  connectedAccountBands: GrantedBandView[];
  controls: GrantedControls;
}

function normalizeControls(raw: GrantedCardControls_Gen, currency: string): GrantedControls {
  return {
    graceDaysAfterEnd: raw.graceDaysAfterEnd,
    affiliate: {
      recurringMaxBp: raw.affiliateRecurringMaxBp,
      bountyMaxMinor: raw.affiliateBountyMaxMinor,
      milestoneMaxMinor: raw.affiliateMilestoneMaxMinor,
      coolOffMaxDays: raw.affiliateCoolOffMaxDays,
      currency,
    },
  };
}

function normalizeCard(body: unknown): GrantedCard {
  const raw = body as GrantedCustomerRateCardView_Gen;
  return {
    id: raw.id,
    name: raw.name,
    currency: raw.currency,
    currentVersionId: raw.currentVersionId,
    adopted: raw.adopted,
    marginMinor: raw.marginMinor,
    hasExample: raw.example !== null,
    baseMinor: raw.baseMinor,
    baseWholesaleMinor: raw.baseWholesaleMinor,
    annualBaseMinor: raw.annualBaseMinor,
    annualBaseWholesaleMinor: raw.annualBaseWholesaleMinor,
    trialDaysNewSubscriber: raw.trialDaysNewSubscriber,
    trialDaysGuestConversion: raw.trialDaysGuestConversion,
    aiRetailMarkupBp: raw.aiRetailMarkupBp,
    aiMonthlyCreditMinor: raw.aiMonthlyCreditMinor,
    aiCreditPlatformShareBp: raw.aiCreditPlatformShareBp,
    aiRechargeAmountMinor: raw.aiRechargeAmountMinor,
    seatBands: raw.seatBands,
    connectedAccountBands: raw.connectedAccountBands,
    controls: normalizeControls(raw.controls, raw.currency),
  };
}

function normalizeCardList(body: unknown): GrantedCard[] {
  const raw = body as GrantedCustomerRateCardList_Gen;
  return raw.rateCards.map(normalizeCard);
}

/** The worksheet always has a mode. The request type also allows null; this screen never sends it. */
export type WorksheetMode = Exclude<WorksheetRequest['mode'], null>;
export type WorksheetLineKind = ResellerWorksheetLine['line'];

/**
 * One worksheet line, per month and signed, exactly as tmm-api sent it (TMMAPI-0154 §H). `line`
 * stays the wire string, so a kind this console doesn't know reaches the screen's generic label.
 */
export interface WorksheetLine {
  line: string;
  customerPaysMinor: number;
  resellerCostsMinor: number;
  resellerProfitMinor: number;
}

/**
 * One line of the customer's bill (`WorksheetBillLine`, TMMAPI-0155), as sent. `kind` stays the
 * wire string (an unknown one gets the generic label); `amountMinor` is signed (the credit is
 * negative). Quantity and unit prices are optional.
 */
export interface BillLine {
  kind: string;
  quantity: number | undefined;
  unitPriceMinor: number | undefined;
  flatMinor: number | undefined;
  amountMinor: number;
}

/**
 * The customer's bill at customer prices (`WorksheetBill`): the subscription subtotal is per month
 * on a MONTH period and per year on a YEAR one; `aiNetMinor` is the AI paid from the wallet.
 */
export interface Bill {
  period: string;
  lines: BillLine[];
  subscriptionSubtotalMinor: number;
  aiNetMinor: number;
}

/** One processing charge behind the reseller's lines (`ResellerWorksheetCharge`), as sent. */
export interface WorksheetStepCharge {
  /** BASE, ADD_ONS or RECHARGE: the wire string, so an unknown kind gets the generic label. */
  kind: string;
  grossMinor: number;
  perYear: number;
  feeMinor: number;
  resellerShareMinor: number;
}

/** The steps behind the reseller's lines (`ResellerWorksheetSteps`), as sent. */
export interface WorksheetSteps {
  subscriptionChargesPerYear: number;
  charges: WorksheetStepCharge[];
  feePercentBp: number | null;
  feeFixedMinor: number | null;
  recharges: number;
  creditCoveredMinor: number;
  creditResellerAbsorbsMinor: number;
}

/**
 * The reseller's worksheet (`ResellerWorksheet`). Only the reseller's three columns exist: the
 * platform's cost and profit are never sent, and never declared here.
 */
export interface Worksheet {
  versionId: number;
  mode: string;
  lines: WorksheetLine[];
  totals: WorksheetLine;
  bill: Bill;
  steps: WorksheetSteps;
  /** The reseller's profit as a share of what the customer pays, in bp; `null` when they pay 0. */
  resellerProfitBp: number | null;
  terms: {
    currency: string;
    includedGuests: number;
    includedConnections: number;
    aiMonthlyCreditMinor: number;
  };
}

function normalizeWorksheetLine(raw: ResellerWorksheetLine): WorksheetLine {
  return {
    line: raw.line,
    customerPaysMinor: raw.customerPaysMinor,
    resellerCostsMinor: raw.resellerCostsMinor,
    resellerProfitMinor: raw.resellerProfitMinor,
  };
}

function normalizeBillLine(raw: WorksheetBillLine): BillLine {
  return {
    kind: raw.kind,
    quantity: raw.quantity,
    unitPriceMinor: raw.unitPriceMinor,
    flatMinor: raw.flatMinor,
    amountMinor: raw.amountMinor,
  };
}

function normalizeBill(raw: WorksheetBill): Bill {
  return {
    period: raw.period,
    lines: raw.lines.map(normalizeBillLine),
    subscriptionSubtotalMinor: raw.subscriptionSubtotalMinor,
    aiNetMinor: raw.aiNetMinor,
  };
}

function normalizeStepCharge(raw: ResellerWorksheetCharge): WorksheetStepCharge {
  return {
    kind: raw.kind,
    grossMinor: raw.grossMinor,
    perYear: raw.perYear,
    feeMinor: raw.feeMinor,
    resellerShareMinor: raw.resellerShareMinor,
  };
}

function normalizeSteps(raw: ResellerWorksheetSteps): WorksheetSteps {
  return {
    subscriptionChargesPerYear: raw.subscriptionChargesPerYear,
    charges: raw.charges.map(normalizeStepCharge),
    feePercentBp: raw.feePercentBp,
    feeFixedMinor: raw.feeFixedMinor,
    recharges: raw.recharges,
    creditCoveredMinor: raw.creditCoveredMinor,
    creditResellerAbsorbsMinor: raw.creditResellerAbsorbsMinor,
  };
}

function normalizeWorksheet(body: unknown): Worksheet {
  const raw = body as ResellerWorksheet;
  return {
    versionId: raw.versionId,
    mode: raw.mode,
    lines: raw.lines.map(normalizeWorksheetLine),
    totals: normalizeWorksheetLine(raw.totals),
    bill: normalizeBill(raw.bill),
    steps: normalizeSteps(raw.steps),
    resellerProfitBp: raw.resellerProfitBp,
    terms: {
      currency: raw.terms.currency,
      includedGuests: raw.terms.includedGuests,
      includedConnections: raw.terms.includedConnections,
      aiMonthlyCreditMinor: raw.terms.aiMonthlyCreditMinor,
    },
  };
}

const RATE_CARDS = '/v1/reseller-admin/rate-cards';
const CUSTOMER_RATE_CARDS = '/v1/reseller-admin/customer-rate-cards';

export const rateCardsApi = {
  async listGrantedCards(): Promise<GrantedCard[]> {
    return normalizeCardList(await request<unknown>('GET', RATE_CARDS));
  },

  async getGrantedCard(id: number): Promise<GrantedCard> {
    return normalizeCard(await request<unknown>('GET', `${RATE_CARDS}/${id}`));
  },

  /** Switches new sales to this card. `409 AGREEMENT_REQUIRED`. */
  async adoptCard(id: number): Promise<GrantedCard> {
    return normalizeCard(await request<unknown>('POST', `${RATE_CARDS}/${id}/adopt`));
  },

  /**
   * A granted card version's margin worksheet for a hypothetical customer. Writes nothing.
   * `400 VALIDATION_FAILED` on a bad body; `404` for a card not granted or a draft version.
   */
  async getWorksheet(id: number, versionId: number, body: WorksheetRequest): Promise<Worksheet> {
    return normalizeWorksheet(
      await request<unknown>('POST', `${CUSTOMER_RATE_CARDS}/${id}/versions/${versionId}/worksheet`, {
        json: body,
      }),
    );
  },
};
